import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Card,
  Table,
  Button,
  Space,
  Select,
  Tag,
  Modal,
  Drawer,
  Descriptions,
  Badge,
  Row,
  Col,
  Statistic,
  Empty,
} from 'antd'
import type { TableProps } from 'antd'
import {
  CheckOutlined,
  EyeOutlined,
  FilterOutlined,
  ReloadOutlined,
  MailOutlined,
  TwitterOutlined,
  SendOutlined,
  SmileOutlined,
  FrownOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  useReplies,
  useMarkReplyProcessed,
  useBatchProcessReplies,
  useUnprocessedRepliesCount,
} from '../hooks'
import { useReplyFilterStore } from '../store'
import type { Reply, OutreachChannel, ReplyIntent } from '../types'

const channelConfig: Record<OutreachChannel, { icon: React.ReactNode; color: string; text: string }> = {
  email: { icon: <MailOutlined />, color: 'blue', text: 'Email' },
  twitter_dm: { icon: <TwitterOutlined />, color: 'cyan', text: 'Twitter DM' },
  twitter_reply: { icon: <TwitterOutlined />, color: 'geekblue', text: 'Twitter Reply' },
  telegram: { icon: <SendOutlined />, color: 'purple', text: 'Telegram' },
  discord: { icon: <SendOutlined />, color: 'magenta', text: 'Discord' },
}

const intentConfig: Record<ReplyIntent, { icon: React.ReactNode; color: string; text: string }> = {
  interested: { icon: <SmileOutlined />, color: 'success', text: '有意向' },
  meeting_request: { icon: <CheckOutlined />, color: 'processing', text: '约会议' },
  question: { icon: <QuestionCircleOutlined />, color: 'blue', text: '有疑问' },
  not_interested: { icon: <FrownOutlined />, color: 'default', text: '无意向' },
  auto_reply: { icon: <RobotOutlined />, color: 'warning', text: '自动回复' },
  spam: { icon: <FrownOutlined />, color: 'error', text: '垃圾' },
  unknown: { icon: <QuestionCircleOutlined />, color: 'default', text: '未知' },
}

function ReplyCenter() {
  const navigate = useNavigate()
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false)
  const [selectedReply, setSelectedReply] = useState<Reply | null>(null)

  // 状态管理
  const { channel, intent, isProcessed, page, pageSize, setFilter, resetFilters } =
    useReplyFilterStore()

  // 数据查询
  const { data, isLoading, refetch } = useReplies({
    channel: channel || undefined,
    intent: intent || undefined,
    is_processed: isProcessed ?? undefined,
    page,
    page_size: pageSize,
  })
  const { data: unprocessedCount } = useUnprocessedRepliesCount()

  // Mutations
  const markProcessed = useMarkReplyProcessed()
  const batchProcess = useBatchProcessReplies()

  // 表格列定义
  const columns: TableProps<Reply>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '项目',
      dataIndex: 'project_id',
      key: 'project_id',
      width: 150,
      render: (projectId: number, record: Reply) => (
        <a onClick={() => navigate(`/projects/${projectId}`)}>
          {record.project?.name || `项目 ${projectId}`}
        </a>
      ),
    },
    {
      title: '渠道',
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (channel: OutreachChannel) => {
        const config = channelConfig[channel]
        return (
          <Tag icon={config?.icon} color={config?.color}>
            {config?.text || channel}
          </Tag>
        )
      },
    },
    {
      title: '意图',
      dataIndex: 'intent',
      key: 'intent',
      width: 100,
      render: (intent: ReplyIntent) => {
        const config = intentConfig[intent]
        return (
          <Tag icon={config?.icon} color={config?.color}>
            {config?.text || intent}
          </Tag>
        )
      },
    },
    {
      title: '置信度',
      dataIndex: 'confidence',
      key: 'confidence',
      width: 80,
      render: (confidence: number) => `${Math.round(confidence * 100)}%`,
    },
    {
      title: '发送者',
      dataIndex: 'sender',
      key: 'sender',
      width: 150,
      ellipsis: true,
    },
    {
      title: '内容预览',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (content: string) => content?.substring(0, 100) || '-',
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (time: string) => dayjs(time).format('MM-DD HH:mm'),
    },
    {
      title: '状态',
      dataIndex: 'is_processed',
      key: 'is_processed',
      width: 80,
      render: (processed: boolean) =>
        processed ? (
          <Badge status="success" text="已处理" />
        ) : (
          <Badge status="warning" text="待处理" />
        ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: unknown, record: Reply) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedReply(record)
              setDetailDrawerOpen(true)
            }}
          >
            详情
          </Button>
          {!record.is_processed && (
            <Button
              type="link"
              size="small"
              icon={<CheckOutlined />}
              onClick={() =>
                markProcessed.mutate({ id: record.id })
              }
            >
              处理
            </Button>
          )}
        </Space>
      ),
    },
  ]

  // 批量处理
  const handleBatchProcess = (intent?: string) => {
    if (selectedRowKeys.length === 0) return
    batchProcess.mutate({ ids: selectedRowKeys as number[], intent })
    setSelectedRowKeys([])
  }

  return (
    <div className="fade-in">
      {/* 页面标题 */}
      <div className="page-header">
        <h1>
          回复处理中心
          {(unprocessedCount?.total || 0) > 0 && (
            <Badge
              count={unprocessedCount?.total}
              style={{ marginLeft: 12 }}
            />
          )}
        </h1>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            刷新
          </Button>
          {selectedRowKeys.length > 0 && (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => handleBatchProcess()}
            >
              批量标记已处理 ({selectedRowKeys.length})
            </Button>
          )}
        </Space>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="待处理"
              value={unprocessedCount?.total || 0}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="有意向"
              value={unprocessedCount?.by_intent?.interested || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="约会议"
              value={unprocessedCount?.by_intent?.meeting_request || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="有疑问"
              value={unprocessedCount?.by_intent?.question || 0}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 筛选区 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="渠道筛选"
            allowClear
            style={{ width: 150 }}
            value={channel}
            onChange={(value) => setFilter('channel', value)}
            options={Object.entries(channelConfig).map(([key, value]) => ({
              value: key,
              label: value.text,
            }))}
          />
          <Select
            placeholder="意图筛选"
            allowClear
            style={{ width: 150 }}
            value={intent}
            onChange={(value) => setFilter('intent', value)}
            options={Object.entries(intentConfig).map(([key, value]) => ({
              value: key,
              label: value.text,
            }))}
          />
          <Select
            placeholder="处理状态"
            allowClear
            style={{ width: 150 }}
            value={isProcessed}
            onChange={(value) => setFilter('isProcessed', value)}
            options={[
              { value: false, label: '待处理' },
              { value: true, label: '已处理' },
            ]}
          />
          <Button icon={<FilterOutlined />} onClick={resetFilters}>
            重置
          </Button>
        </Space>
      </Card>

      {/* 表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={data?.items}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1300 }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (p, ps) => {
              setFilter('page', p)
              if (ps !== pageSize) setFilter('pageSize', ps)
            },
          }}
        />
      </Card>

      {/* 详情抽屉 */}
      <Drawer
        title="回复详情"
        open={detailDrawerOpen}
        onClose={() => {
          setDetailDrawerOpen(false)
          setSelectedReply(null)
        }}
        width={600}
        extra={
          selectedReply && !selectedReply.is_processed && (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => {
                markProcessed.mutate({ id: selectedReply.id })
                setDetailDrawerOpen(false)
              }}
            >
              标记已处理
            </Button>
          )
        }
      >
        {selectedReply ? (
          <div>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="项目">
                <a onClick={() => navigate(`/projects/${selectedReply.project_id}`)}>
                  {selectedReply.project?.name || `项目 ${selectedReply.project_id}`}
                </a>
              </Descriptions.Item>
              <Descriptions.Item label="渠道">
                <Tag
                  icon={channelConfig[selectedReply.channel]?.icon}
                  color={channelConfig[selectedReply.channel]?.color}
                >
                  {channelConfig[selectedReply.channel]?.text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="意图">
                <Tag
                  icon={intentConfig[selectedReply.intent]?.icon}
                  color={intentConfig[selectedReply.intent]?.color}
                >
                  {intentConfig[selectedReply.intent]?.text}
                </Tag>
                <span style={{ marginLeft: 8, color: 'rgba(0,0,0,0.45)' }}>
                  置信度: {Math.round(selectedReply.confidence * 100)}%
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="发送者">
                {selectedReply.sender || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="时间">
                {dayjs(selectedReply.created_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="处理状态">
                {selectedReply.is_processed ? (
                  <Badge status="success" text="已处理" />
                ) : (
                  <Badge status="warning" text="待处理" />
                )}
              </Descriptions.Item>
              {selectedReply.processed_by && (
                <Descriptions.Item label="处理人">
                  {selectedReply.processed_by}
                </Descriptions.Item>
              )}
              {selectedReply.processed_at && (
                <Descriptions.Item label="处理时间">
                  {dayjs(selectedReply.processed_at).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Card
              title="回复内容"
              size="small"
              style={{ marginTop: 16, background: '#fafafa' }}
            >
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                {selectedReply.content}
              </pre>
            </Card>

            {selectedReply.outreach_record && (
              <Card title="原始触达" size="small" style={{ marginTop: 16 }}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="阶段">
                    Phase {selectedReply.outreach_record.phase}
                  </Descriptions.Item>
                  <Descriptions.Item label="发送时间">
                    {selectedReply.outreach_record.sent_at
                      ? dayjs(selectedReply.outreach_record.sent_at).format('YYYY-MM-DD HH:mm')
                      : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="内容预览">
                    {selectedReply.outreach_record.content?.substring(0, 200)}...
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}
          </div>
        ) : (
          <Empty description="暂无数据" />
        )}
      </Drawer>
    </div>
  )
}

export default ReplyCenter
